import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  Clock, 
  Target, 
  Award,
  CheckCircle,
  BarChart3,
  BookMarked,
  Users,
  Globe,
  DollarSign
} from 'lucide-react';
import { useAppState } from '../context/AppStateContext';
import StatisticsNavigator from './StatisticsNavigator';
import NovelStatistics from './NovelStatistics';

type TabType = 'overview' | 'novel' | 'manga' | 'global';

const Statistics: React.FC = () => {
  const { state } = useAppState();
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const statistics = useMemo(() => {
    // Filtrar libros excluyendo wishlist para estadísticas
    const librosParaEstadisticas = state.libros.filter(book => book.estado !== 'wishlist');
    
    // Calculate stats based on book states
    const totalLibros = librosParaEstadisticas.length;
    const librosTBR = librosParaEstadisticas.filter(book => book.estado === 'tbr').length;
    const librosLeyendo = librosParaEstadisticas.filter(book => book.estado === 'leyendo').length;
    const librosLeidos = librosParaEstadisticas.filter(book => book.estado === 'leido').length;
    const librosAbandonados = librosParaEstadisticas.filter(book => book.estado === 'abandonado').length;
    const librosWishlist = state.libros.filter(book => book.estado === 'wishlist').length;
    const librosPrestados = librosParaEstadisticas.filter(book => book.prestado).length;
    const librosPrestadosDetalle = librosParaEstadisticas.filter(book => book.prestado);
    
    const sagasCompletadas = state.sagas.filter(s => s.isComplete).length;
    const sagasActivas = state.sagas.filter(s => !s.isComplete).length;
    
    // Calculate pages read from completed books
    const paginasLeidas = state.libros
      .filter(book => book.estado === 'leido')
      .reduce((sum, book) => sum + (book.paginas || 0), 0);
    
    // Calculate reading progress
    const progresoLectura = totalLibros > 0 ? (librosLeidos / totalLibros) * 100 : 0;
    
    // Calculate average rating
    const librosConCalificacion = state.libros.filter(book => book.calificacion && book.calificacion > 0);
    const calificacionPromedio = librosConCalificacion.length > 0 
      ? librosConCalificacion.reduce((sum, book) => sum + (book.calificacion || 0), 0) / librosConCalificacion.length
      : 0;
    
    // Calculate total collection value (excluyendo wishlist)
    const valorTotalColeccion = state.libros
      .filter(book => book.estado !== 'wishlist' && book.precio && book.precio > 0)
      .reduce((sum, book) => sum + (book.precio || 0), 0);
    
    // Calculate average book price (excluyendo wishlist)
    const librosConPrecio = state.libros.filter(book => book.estado !== 'wishlist' && book.precio && book.precio > 0);
    const precioPromedio = librosConPrecio.length > 0 
      ? librosConPrecio.reduce((sum, book) => sum + (book.precio || 0), 0) / librosConPrecio.length
      : 0;
    
    // Calculate reading speed (pages per day)
    const librosConTiempoLectura = state.libros
      .filter(book => book.estado === 'leido' && book.fechaInicio && book.fechaFin && book.paginas)
      .map(book => {
        const diasLectura = (book.fechaFin! - book.fechaInicio!) / (1000 * 60 * 60 * 24);
        return {
          paginas: book.paginas!,
          dias: Math.max(1, diasLectura) // Mínimo 1 día para evitar división por cero
        };
      });
    
    const velocidadLectura = librosConTiempoLectura.length > 0
      ? librosConTiempoLectura.reduce((sum, libro) => sum + (libro.paginas / libro.dias), 0) / librosConTiempoLectura.length
      : 0;
    
    // Calculate books by format
    const librosPorFormato = state.libros.reduce((acc, book) => {
      const formato = book.formato || 'fisico';
      acc[formato] = (acc[formato] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    // Calculate average pages per book
    const librosConPaginas = state.libros.filter(book => book.paginas && book.paginas > 0);
    const paginasPromedio = librosConPaginas.length > 0
      ? librosConPaginas.reduce((sum, book) => sum + (book.paginas || 0), 0) / librosConPaginas.length
      : 0;
    
    // Calculate completion rate by genre
    const generosConCompletados = state.libros
      .filter(book => book.genero && book.estado === 'leido')
      .reduce((acc, book) => {
        if (!acc[book.genero!]) {
          acc[book.genero!] = { completados: 0, total: 0 };
        }
        acc[book.genero!].completados++;
        return acc;
      }, {} as Record<string, { completados: number; total: number }>);
    
    // Add total books per genre
    state.libros.forEach(book => {
      if (book.genero) {
        if (!generosConCompletados[book.genero]) {
          generosConCompletados[book.genero] = { completados: 0, total: 0 };
        }
        generosConCompletados[book.genero].total++;
      }
    });
    
    const generosConMejorTasa = Object.entries(generosConCompletados)
      .filter(([, stats]) => stats.total >= 2) // Solo géneros con al menos 2 libros
      .map(([genero, stats]) => ({
        genero,
        tasaCompletado: (stats.completados / stats.total) * 100
      }))
      .sort((a, b) => b.tasaCompletado - a.tasaCompletado)
      .slice(0, 3);
    
    // Most read authors
    const autoresCont = state.libros
      .filter(book => book.autor && book.estado === 'leido')
      .reduce((acc, book) => {
        const autor = book.autor!;
        acc[autor] = (acc[autor] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
    
    const autoresMasLeidos = Object.entries(autoresCont)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([autor, count]) => ({ autor, count }));
    
    // Most read genres
    const generosCont = state.libros
      .filter(book => book.genero && book.estado === 'leido')
      .reduce((acc, book) => {
        const genero = book.genero!;
        acc[genero] = (acc[genero] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
    
    const generosMasLeidos = Object.entries(generosCont)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([genero, count]) => ({ genero, count }));
    
    // Reading goal progress
    const objetivoAnual = state.config.objetivoLecturaAnual || 0;
    const progresoObjetivo = objetivoAnual > 0 ? (librosLeidos / objetivoAnual) * 100 : 0;
    
    // Estadísticas de manga
    const totalColeccionesManga = state.coleccionesManga.length; // Número de colecciones
    const totalTomosMangaIndividuales = state.coleccionesManga.reduce((sum, c) => sum + c.tomos.length, 0); // Total de tomos individuales
    const coleccionesMangaCompletadas = state.coleccionesManga.filter(c => c.isComplete).length;
    const totalTomosManga = state.coleccionesManga.reduce((sum, c) => sum + c.tomos.length, 0); // Tomos que realmente tienes
    const tomosMangaLeidos = state.coleccionesManga.reduce((sum, c) => sum + c.tomosLeidos, 0);
    const tomosMangaComprados = state.coleccionesManga.reduce((sum, c) => sum + c.tomosComprados, 0);
    // Calcular valor total de manga basado en los tomos que realmente tienes
    const valorTotalManga = state.coleccionesManga.reduce((sum, coleccion) => {
      if (coleccion.precioPorTomo && coleccion.precioPorTomo > 0) {
        // Contar tomos que realmente tienes (con fechaCompra o en estado comprado/leido/leyendo)
        const tomosReales = coleccion.tomos.filter(tomo => 
          tomo.fechaCompra || 
          tomo.estado === 'comprado' || 
          tomo.estado === 'leido' || 
          tomo.estado === 'leyendo'
        ).length;
        return sum + (tomosReales * coleccion.precioPorTomo);
      }
      return sum;
    }, 0);
    
    return {
      totalLibros,
      librosTBR,
      librosLeyendo,
      librosLeidos,
      librosAbandonados,
      librosWishlist,
      librosPrestados,
      librosPrestadosDetalle,
      sagasCompletadas,
      sagasActivas,
      paginasLeidas,
      progresoLectura,
      calificacionPromedio,
      autoresMasLeidos,
      generosMasLeidos,
      objetivoAnual,
      progresoObjetivo,
      valorTotalColeccion,
      precioPromedio,
      velocidadLectura,
      librosPorFormato,
      paginasPromedio,
      generosConMejorTasa,
      // Estadísticas de manga
      totalColeccionesManga,
      totalTomosMangaIndividuales,
      coleccionesMangaCompletadas,
      totalTomosManga,
      tomosMangaLeidos,
      tomosMangaComprados,
      valorTotalManga
    };
  }, [state.libros, state.sagas, state.coleccionesManga, state.config.objetivoLecturaAnual]);

  const tabs = [
    {
      id: 'overview' as TabType,
      label: 'Vista General',
      icon: BarChart3,
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'novel' as TabType,
      label: 'Estadísticas de Novela',
      icon: BookOpen,
      color: 'from-indigo-500 to-indigo-600'
    },
    {
      id: 'manga' as TabType,
      label: 'Estadísticas de Manga',
      icon: BookMarked,
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'global' as TabType,
      label: 'Estadísticas Globales',
      icon: Globe,
      color: 'from-green-500 to-green-600'
    }
  ];

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center space-x-2 mb-4"
        >
          <BarChart3 className="h-8 w-8 text-primary-500" />
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
            Estadísticas de la Biblioteca
          </h1>
        </motion.div>
        <p className="text-slate-600 dark:text-slate-400">
          Análisis detallado de tu progreso de lectura
        </p>
      </div>

      {/* Métricas principales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-effect rounded-xl p-6 text-center"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 mb-4">
            <BookOpen className="h-8 w-8 text-white" />
          </div>
          <div className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            {statistics.totalLibros + statistics.totalTomosMangaIndividuales}
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400">
            Total en Biblioteca
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">
            {statistics.totalLibros} libros + {statistics.totalTomosMangaIndividuales} tomos
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-effect rounded-xl p-6 text-center"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-gradient-to-br from-green-500 to-green-600 mb-4">
            <CheckCircle className="h-8 w-8 text-white" />
          </div>
          <div className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            {statistics.librosLeidos + statistics.tomosMangaLeidos}
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400">
            Completados
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">
            {statistics.librosLeidos} libros + {statistics.tomosMangaLeidos} tomos
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-effect rounded-xl p-6 text-center"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-gradient-to-br from-yellow-500 to-yellow-600 mb-4">
            <Clock className="h-8 w-8 text-white" />
          </div>
          <div className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            {statistics.librosTBR + statistics.totalTomosManga - statistics.tomosMangaLeidos}
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400">
            Por Leer
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">
            TBR y manga pendiente
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass-effect rounded-xl p-6 text-center"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 mb-4">
            <DollarSign className="h-8 w-8 text-white" />
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            {(statistics.valorTotalColeccion + statistics.valorTotalManga).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400">
            Valor Total
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">
            Inversión en la colección
          </div>
        </motion.div>
      </div>

      {/* Progreso y objetivos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="glass-effect rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <Target className="h-5 w-5 mr-2 text-blue-500" />
            Progreso General
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">Progreso de Lectura</span>
              <span className="text-sm font-medium text-slate-900 dark:text-white">
                {((statistics.librosLeidos + statistics.tomosMangaLeidos) / (statistics.totalLibros + statistics.totalTomosMangaIndividuales) * 100).toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                style={{ 
                  width: `${Math.min(100, (statistics.librosLeidos + statistics.tomosMangaLeidos) / (statistics.totalLibros + statistics.totalTomosMangaIndividuales) * 100)}%` 
                }}
              />
            </div>
            <div className="text-center text-sm text-slate-600 dark:text-slate-400">
              {statistics.librosLeidos + statistics.tomosMangaLeidos} de {statistics.totalLibros + statistics.totalTomosMangaIndividuales} completados
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="glass-effect rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <Award className="h-5 w-5 mr-2 text-green-500" />
            Objetivo Anual
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">Progreso del Objetivo</span>
              <span className="text-sm font-medium text-slate-900 dark:text-white">
                {statistics.progresoObjetivo.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500"
                style={{ 
                  width: `${Math.min(100, statistics.progresoObjetivo)}%` 
                }}
              />
            </div>
            <div className="text-center text-sm text-slate-600 dark:text-slate-400">
              {statistics.librosLeidos + statistics.tomosMangaLeidos} de {statistics.objetivoAnual} objetivo
            </div>
          </div>
        </motion.div>
      </div>

      {/* Estadísticas detalladas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="glass-effect rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <BookOpen className="h-5 w-5 mr-2 text-purple-500" />
            Estado de Libros
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">Leyendo</span>
              <span className="text-sm font-medium text-slate-900 dark:text-white">{statistics.librosLeyendo}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">TBR</span>
              <span className="text-sm font-medium text-slate-900 dark:text-white">{statistics.librosTBR}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">Abandonados</span>
              <span className="text-sm font-medium text-slate-900 dark:text-white">{statistics.librosAbandonados}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">Prestados</span>
              <span className="text-sm font-medium text-slate-900 dark:text-white">{statistics.librosPrestados}</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="glass-effect rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <BookMarked className="h-5 w-5 mr-2 text-indigo-500" />
            Estado de Manga
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">Colecciones</span>
              <span className="text-sm font-medium text-slate-900 dark:text-white">{statistics.totalColeccionesManga}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">Total Tomos</span>
              <span className="text-sm font-medium text-slate-900 dark:text-white">{statistics.totalTomosMangaIndividuales}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">Tomos Leídos</span>
              <span className="text-sm font-medium text-slate-900 dark:text-white">{statistics.tomosMangaLeidos}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">Completadas</span>
              <span className="text-sm font-medium text-slate-900 dark:text-white">{statistics.coleccionesMangaCompletadas}</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="glass-effect rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2 text-teal-500" />
            Sagas
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">Completadas</span>
              <span className="text-sm font-medium text-slate-900 dark:text-white">{statistics.sagasCompletadas}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">Activas</span>
              <span className="text-sm font-medium text-slate-900 dark:text-white">{statistics.sagasActivas}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">Total</span>
              <span className="text-sm font-medium text-slate-900 dark:text-white">{statistics.sagasCompletadas + statistics.sagasActivas}</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'novel':
        return <NovelStatistics />;
      case 'manga':
        return <StatisticsNavigator initialView="manga" hideHeader={true} />;
      case 'global':
        return <StatisticsNavigator initialView="global" hideHeader={true} />;
      default:
        return renderOverviewTab();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header principal */}
      <div className="text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center space-x-2 mb-4"
        >
          <BarChart3 className="h-8 w-8 text-primary-500" />
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
            Centro de Estadísticas
          </h1>
        </motion.div>
        <p className="text-slate-600 dark:text-slate-400">
          Analiza tu biblioteca desde múltiples perspectivas
        </p>
      </div>

      {/* Pestañas de navegación */}
      <div className="flex flex-wrap justify-center gap-2">
        {tabs.map((tab) => (
          <motion.button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
              activeTab === tab.id
                ? `bg-gradient-to-r ${tab.color} text-white shadow-lg transform scale-105`
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <tab.icon className="h-5 w-5" />
            <span>{tab.label}</span>
          </motion.button>
        ))}
      </div>

      {/* Contenido de la pestaña activa */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {renderContent()}
      </motion.div>
    </div>
  );
};

export default Statistics;